"use client";

import type { ReactNode } from "react";
import { Badge, Button, Empty, Result, Skeleton, Space, Tag, Typography } from "antd";
import { ReloadOutlined } from "@ant-design/icons";
import type { AlertSeverity, AlertStatus, HealthStatus } from "../domain/models";

const healthMeta: Record<HealthStatus, { color: string; label: string }> = {
  healthy: { color: "success", label: "正常" },
  warning: { color: "warning", label: "警告" },
  critical: { color: "error", label: "异常" },
  offline: { color: "default", label: "离线" },
};

export function HealthTag({ status }: { status: HealthStatus }) {
  const meta = healthMeta[status];
  return <Tag color={meta.color} className={`status-tag status-${status}`}><Badge status={status === "healthy" ? "success" : status === "warning" ? "warning" : status === "critical" ? "error" : "default"} />{meta.label}</Tag>;
}

const severityMeta: Record<AlertSeverity, { color: string; label: string }> = {
  critical: { color: "red", label: "严重" },
  major: { color: "orange", label: "重要" },
  minor: { color: "gold", label: "一般" },
  info: { color: "blue", label: "提示" },
};

export function SeverityTag({ severity }: { severity: AlertSeverity }) {
  const meta = severityMeta[severity];
  return <Tag color={meta.color}>{meta.label}</Tag>;
}

const alertStatusMeta: Record<AlertStatus, { color: string; label: string }> = {
  firing: { color: "red", label: "告警中" },
  acknowledged: { color: "blue", label: "已确认" },
  processing: { color: "processing", label: "处理中" },
  resolved: { color: "success", label: "已恢复" },
};

export function AlertStatusTag({ status }: { status: AlertStatus }) {
  const meta = alertStatusMeta[status];
  return <Tag color={meta.color}>{meta.label}</Tag>;
}

export function PageHeader({ title, description, extra }: { title: string; description: string; extra?: ReactNode }) {
  return <div className="page-heading"><div><Typography.Title level={3}>{title}</Typography.Title><Typography.Text type="secondary">{description}</Typography.Text></div>{extra && <Space>{extra}</Space>}</div>;
}

export function PageLoading() {
  return <div className="surface-card padded-card"><Skeleton active paragraph={{ rows: 8 }} /></div>;
}

export function PageError({ message, onRetry }: { message: string; onRetry?: () => void }) {
  return <Result status="error" title="数据加载失败" subTitle={message} extra={onRetry && <Button icon={<ReloadOutlined />} onClick={onRetry}>重新加载</Button>} />;
}

export function EmptyState({ description = "当前筛选条件下没有数据" }: { description?: string }) {
  return <Empty image={Empty.PRESENTED_IMAGE_SIMPLE} description={description} />;
}

export const serviceName = (id: string, data: Array<{ id: string; name: string }>) => data.find((item) => item.id === id)?.name ?? id;
